import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link, useNavigate } from "react-router";
import {
  LuGlobe, LuSmartphone, LuPalette, LuShoppingCart, LuServer, LuCode,
  LuLightbulb, LuShieldCheck, LuCloud, LuLayers, LuWrench,
} from "react-icons/lu";
import { Images } from "../controller/Images";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone, companyServices, workflowsteps, chooseus, serviceCategories } from "../controller/global";
import { submitLead } from "../controller/leads";

// Same trust-logo set shown on /generative-ai-development, /about, and the
// other AI service pages.
const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

// Real service icons — no photography, matching the rest of the site.
const SERVICE_ICONS = {
  web_development: LuGlobe,
  mobile_app_development: LuSmartphone,
  ui_ux: LuPalette,
  ecommerce_development: LuShoppingCart,
  cloud_devops_development: LuServer,
  payment_getway_development: LuCode,
  it_consulting: LuLightbulb,
  cyber_security: LuShieldCheck,
  cloud_development: LuCloud,
  cloud_native_applications: LuLayers,
  product_support: LuWrench,
};

const APPROACH = [
  {
    num: "01 / FULL-STACK",
    title: "One team, the whole stack.",
    copy: "From the first wireframe to production infrastructure, the same team owns delivery end to end — no hand-offs between vendors.",
    tags: ["Web & mobile", "Cloud & DevOps", "Security & compliance"],
  },
  {
    num: "02 / PROCESS",
    title: "The same process, every service.",
    copy: "Every one of our eleven services follows the same seven-stage delivery process, so quality doesn't depend on which service you picked.",
    tags: ["Discovery", "Build", "Launch & support"],
  },
  {
    num: "03 / AI-READY",
    title: "Built to plug into AI, when you're ready.",
    copy: "Every service we build is architected so you can add generative AI, agents, or recommendation systems later without a rebuild.",
    tags: ["Generative AI", "Agentic AI", "AI Integration"],
  },
];

const PROCESS_STEPS = workflowsteps.map((w) => ({ n: w.number, t: w.title, d: w.description }));

const FIT_CRITERIA = [
  { a: "Need a customer-facing website or app?", b: "WEB & MOBILE" },
  { a: "Need to scale or secure your infrastructure?", b: "CLOUD & SECURITY" },
  { a: "Need to modernize legacy systems?", b: "DIGITAL TRANSFORMATION" },
  { a: "Need ongoing support post-launch?", b: "PRODUCT SUPPORT" },
];

const FACTS = [
  { a: "11", b: "Core services offered" },
  { a: "7", b: "Stage delivery process" },
  { a: "MSME", b: "Registered IT company" },
  { a: "Delhi NCR", b: "Headquarters" },
];

const ENGAGEMENT_RESULTS = [
  { a: "Fixed-Scope", b: "Clearly defined deliverable" },
  { a: "Dedicated Team", b: "Ongoing, evolving product work" },
  { a: "MVP Sprint", b: "Validate an idea fast" },
  { a: "1", b: "Same process, every model" },
];

const WHO_WE_BUILD_FOR = [
  { a: "Early-stage startups", b: "From idea to a working MVP in weeks, not quarters." },
  { a: "Growing SMBs", b: "Systems that scale as your team and customer base grow." },
  { a: "Enterprises", b: "Modernization, integration, and governance for complex systems." },
  { a: "E-commerce brands", b: "Storefronts, checkout, and inventory systems built to convert." },
  { a: "SaaS product teams", b: "A dedicated team that ships alongside your own engineers." },
  { a: "Agencies & consultancies", b: "Extra engineering capacity for client work, working directly with your team." },
  { a: "Non-profits & public sector", b: "Reliable, well-documented systems built within real budgets." },
  { a: "Internal platform teams", b: "Cloud and DevOps support for the tools your company runs on." },
];

const FAQS = [
  {
    q: "What services does InQmise Technologies offer?",
    a: "Web development, mobile app development, UI/UX design, e-commerce, cloud and DevOps, cybersecurity, API development, and AI development — every service on this page has its own dedicated page with real detail.",
  },
  {
    q: "Do you build AI-powered applications?",
    a: "Yes — we have dedicated services for Generative AI Development, Agentic AI Development, Recommendation Engine Development, and AI Integration Services, each with its own page describing what we build.",
  },
  {
    q: "Do you offer support after a project launches?",
    a: "Yes — our Product Support & Maintenance service covers ongoing updates, bug fixes, and 24/7 assistance so your product keeps running smoothly after launch.",
  },
  {
    q: "What engagement models do you offer?",
    a: "We work Fixed-Scope, Dedicated Team, or MVP Sprint — the same process and team stay accountable from kickoff to launch regardless of which model fits your project.",
  },
  {
    q: "Where is your team based?",
    a: "We're an MSME-registered IT company headquartered in Delhi NCR, following the same 7-stage delivery process across every engagement.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the engine panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

// Same section-for-section structure as /generative-ai-development: hero +
// engine panel, trust logos, feature rows, process, service cards, a dark
// fit panel, an engagement-model spotlight, a "who we build for" grid, an
// inline FAQ, and a contact CTA with an embedded form — applied here with
// the real 11 non-AI services, no photography anywhere on the page.
const Services = () => {
  const navigate = useNavigate();
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = "Software Development Services in Delhi NCR | InQmise";

    const description = document.createElement("meta");
    description.name = "description";
    description.content = "Software development services in Delhi NCR — custom software, web and mobile apps, UI/UX, cloud, DevOps, and AI integration for growing businesses.";
    document.head.appendChild(description);

    const keywords = document.createElement("meta");
    keywords.name = "keywords";
    keywords.content = "custom software development Delhi NCR, mobile app development company Delhi NCR, web development services Delhi, Android app development Delhi, iOS app development NCR, ERP software development Delhi, UI UX design company Delhi NCR, API development services Delhi, SaaS product development Delhi, e-commerce website development Delhi NCR, digital transformation services Delhi, software outsourcing company Delhi, DevOps services company Delhi, cloud development services, cloud native applications, cloud infrastructure Delhi";
    document.head.appendChild(keywords);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/services");

    // The company entity (@id #organization) is declared once, in index.html;
    // restating it here with a different url and areaServed made Google merge
    // conflicting copies. What this page adds is the catalogue of services,
    // attached to that entity by reference.
    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "OfferCatalog",
      "name": "Software development services",
      "url": "https://inqmisetechnologies.com/services",
      "itemListElement": companyServices.map((s) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": s.title,
          "url": `https://inqmisetechnologies.com${s.path}`,
          "provider": { "@id": "https://inqmisetechnologies.com/#organization" },
          "areaServed": ["IN", "US", "GB", "CA", "AU", "AE", "SG"],
        },
      })),
    });
    document.head.appendChild(schemaScript);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname":
        return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject":
        return value.trim() ? "" : "Subject is required.";
      case "description":
        return value.trim() ? "" : "Tell us a little about your project.";
      default:
        return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }

    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => {
        setContactStatus("error");
      });
  };

  const openContactModal = () => setIsOpen(true);

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">Full-service software development · built to scale</p>
          <h1>
            Software development services that <em>fit how you actually build</em>.
          </h1>
          <p className="ea-lede">
            Software development services from web and mobile to cloud, security, and AI — eleven core services, one accountable team, and the same seven-stage process behind every engagement.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your project">
              Start your project ↗
            </button>
            <a className="ea-text-link" href="#services">See all services</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / SERVICE CATALOG <b>LIVE</b></div>
          <h2>Eleven services, one accountable team.</h2>
          <div className="ea-query"><small>CORE STACK</small>Web, mobile, UI/UX, cloud, DevOps, and cybersecurity — all in-house.</div>
          <div className="ea-query"><small>AI-READY</small>Every engagement can plug into our AI solutions when you're ready.</div>
          <div className="ea-flow"><b>Scope</b><i>→</i><b>Build</b><i>→</i><b>Support</b></div>
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* HOW WE OPERATE — feature rows */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">How we operate</div>
            <h2>One team, the<br />whole stack.</h2>
          </div>
          <p>Every service follows the same discipline, regardless of scope.</p>
        </Reveal>

        {APPROACH.map((item, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={item.title}>
            <div className="ea-num">{item.num}</div>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
            <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </Reveal>
        ))}
      </section>

      {/* PROCESS */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">How we work</div>
            <h2>Discover, build,<br />then support.</h2>
          </div>
          <p>The same disciplined approach across all eleven services.</p>
        </Reveal>
        <div className="ea-process">
          {PROCESS_STEPS.map((step, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-step" key={step.n}>
              <span>{step.n}</span>
              <i>↗</i>
              <h3>{step.t}</h3>
              <p>{step.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES — the real 11 company services */}
      <section className="ea-services" id="services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Our services</div>
              <h2>Eleven services.<br />One accountable team.</h2>
            </div>
            <p>Each service has its own dedicated page with real detail on approach, process, and fit.</p>
          </Reveal>
          <div className="ea-cards">
            {companyServices.map((service, i) => {
              const Icon = SERVICE_ICONS[service.key] || LuLayers;
              return (
                <Reveal
                  as="a"
                  i={(i % 4) + 1}
                  reduceMotion={reduceMotion}
                  key={service.key}
                  className={`ea-card ${i === 0 ? "ea-featured" : ""}`}
                  href={service.path}
                  onClick={(e) => { e.preventDefault(); navigate(service.path); }}
                  style={{ textDecoration: "none" }}
                >
                  <div className="ea-icon"><Icon aria-hidden="true" /></div>
                  <div>
                    <h3>{service.title}</h3>
                    <p>{service.description[1]}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FULL SERVICE INDEX — every service page in one crawlable place.
          The mega menu in the navbar carries these same links, but it only
          mounts once a visitor opens it, so neither a crawler nor a visitor
          who never hovers it ever sees them. Several service pages had a
          single inbound link site-wide because of that. */}
      <section className="ea-wrap ea-section" id="service-index">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Everything we do</div>
            <h2>The full<br />service index.</h2>
          </div>
          <p>Every specialism we run, grouped the way our teams are. Pick the closest fit — if nothing matches exactly, tell us what you are building and we will point you to the right team.</p>
        </Reveal>
        <div className="ea-index-grid">
          {serviceCategories.map((category, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-index-group" key={category.title}>
              <h3>{category.title}</h3>
              <ul>
                {category.items.map((item) => (
                  <li key={item.title}>
                    <Link to={item.path}>{item.title}</Link>
                    {item.facets?.length > 0 && (
                      <ul>
                        {item.facets
                          .filter((facet) => facet.path !== item.path)
                          .map((facet) => (
                            <li key={facet.title}><Link to={facet.path}>{facet.title}</Link></li>
                          ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHICH SERVICE FITS + FACTS */}
      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Not sure where to start?</div>
              <h2>Match your need<br />to a service.</h2>
            </div>
            <p>The fastest way to find the right starting point.</p>
          </Reveal>
          <div className="ea-model-layout">
            <Reveal as="div" className="ea-model-panel">
              <h3>Which service fits?</h3>
              {FIT_CRITERIA.map((row) => (
                <div key={row.a}><span>{row.a}</span><small>{row.b}</small></div>
              ))}
            </Reveal>
            <div className="ea-metrics">
              {FACTS.map((m, i) => (
                <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}>
                  <strong>{m.a}</strong>
                  <span>{m.b}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ENGAGEMENT MODELS — case-study-shaped spotlight */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Engagement models</div>
            <h2>Three ways to work<br />with us.</h2>
          </div>
          <p>One accountable team, whichever model fits your project.</p>
        </Reveal>
        <Reveal as="div" className="ea-case">
          <div>
            <div className="ea-eyebrow">FIXED-SCOPE / DEDICATED / MVP</div>
            <h3>Same process, one accountable team.</h3>
            <p>Whether you need a clearly scoped deliverable, an ongoing dedicated team, or a fast MVP to validate an idea, the same seven-stage process and the same team stay accountable from kickoff to launch.</p>
            <button className="ea-text-link" onClick={openContactModal}>Discuss your project</button>
          </div>
          <div className="ea-results">
            {ENGAGEMENT_RESULTS.map((r) => (
              <div key={r.a}><b>{r.a}</b><span>{r.b}</span></div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* WHO WE BUILD FOR */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Who we build for</div>
            <h2>Built around the<br />stage you're at.</h2>
          </div>
          <p>From a first MVP to a growing enterprise platform, the same team scales with you.</p>
        </Reveal>
        <div className="ea-industries">
          {WHO_WE_BUILD_FOR.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ea-industry" key={item.a}>
              <b>{item.a}</b>
              <p>{item.b}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US — real chooseus values */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Why choose us</div>
            <h2>What sets our<br />team apart.</h2>
          </div>
          <p>The same standards behind every service we deliver.</p>
        </Reveal>
        <div className="ea-cards">
          {chooseus.map((item, i) => (
            <Reveal as="article" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ea-card" key={item.title}>
              <div className="ea-icon"><item.icon aria-hidden="true" /></div>
              <div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>Software development services FAQs</h2>
            <p>Short answers to the questions we hear most.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `services-faq-panel-${i}`;
            const buttonId = `services-faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  >
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="services-contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">A practical first step</div>
            <h2 id="services-contact-heading">Ready to transform your business?</h2>
            <p>Tell us what you're building and we'll help you figure out the right service — or combination of services.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span>
            <h3>Start your project.</h3>
            <p>Tell us what you're building and we'll get back to you within one business day.</p>

            <form onSubmit={handleContactSubmit} aria-label="Services page project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="services-fullname">Full Name</label>
                <input
                  id="services-fullname"
                  name="fullname"
                  type="text"
                  value={formData.fullname}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)}
                  aria-describedby={contactErrors.fullname ? "services-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="services-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="services-phone">Phone Number (optional)</label>
                  <input
                    id="services-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)}
                    aria-describedby={contactErrors.phone ? "services-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="services-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="services-email">Email</label>
                  <input
                    id="services-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)}
                    aria-describedby={contactErrors.email ? "services-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="services-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="services-subject">Subject</label>
                <input
                  id="services-subject"
                  name="subject"
                  type="text"
                  placeholder="e.g., A new web platform for our business"
                  value={formData.subject}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)}
                  aria-describedby={contactErrors.subject ? "services-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="services-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="services-description">Project Details</label>
                <textarea
                  id="services-description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)}
                  aria-describedby={contactErrors.description ? "services-description-error" : undefined}
                />
                {contactErrors.description && <p id="services-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Start a conversation ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Services;
